import { useState, useEffect } from 'react';
import { useApi } from '../hooks/useApi';
import { money } from '../events/util';
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine,
} from 'recharts';

const RED = '#C8102E', RED2 = '#8B0F1A', INK = '#9A9A9A', GRID = '#2A2A2A';
const PIE = ['#C8102E', '#8B0F1A', '#E63946', '#6B0F1A', '#A8101F', '#4A0810'];

function toCSV(rows) {
  if (!rows.length) return '';
  const keys = Object.keys(rows[0]);
  const esc = v => `"${String(v ?? '').replace(/"/g, '""')}"`;
  return [keys.join(','), ...rows.map(r => keys.map(k => esc(r[k])).join(','))].join('\n');
}
function download(name, content, type = 'text/csv') {
  const blob = new Blob([content], { type });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = name; a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}

export default function Analitica() {
  const api = useApi();
  const [tab, setTab] = useState('evento');
  const [events, setEvents] = useState([]);

  useEffect(() => { api.get('/api/events').then(setEvents).catch(() => {}); }, []); // eslint-disable-line

  return (
    <div className="lb-fade">
      <div className="lb-subtabs" style={{ marginBottom: 24 }}>
        <button className={tab === 'evento' ? 'active' : ''} onClick={() => setTab('evento')}>Por evento</button>
        <button className={tab === 'anual' ? 'active' : ''} onClick={() => setTab('anual')}>Anual</button>
        <button className={tab === 'api' ? 'active' : ''} onClick={() => setTab('api')}>API REST</button>
      </div>
      {tab === 'evento' && <PorEvento api={api} events={events} />}
      {tab === 'anual' && <Anual api={api} />}
      {tab === 'api' && <ApiSection api={api} />}
    </div>
  );
}

function Card({ label, value, sub, accent }) {
  return (
    <div className="lb-card" style={{ padding: '20px 22px' }}>
      <div className="lb-eyebrow">{label}</div>
      <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 26, color: accent ? RED : '#fff', marginTop: 6 }}>{value}</div>
      {sub && <div style={{ fontSize: 11.5, color: INK, marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

// VENTA POR DÍA.
//
// Antes este gráfico mostraba `timeline`, que son los QR EMITIDOS: el comprador de un
// link puede sacar su QR semanas después de que el RRPP cobró, así que la curva no era
// la de la venta y su suma no daba el total de vendidas de arriba.
//
// Ahora usa la curva de venta real (misma definición que el total: el link del RRPP el
// día que se generó, online y boletería el día del pago, la mesa el día que se vendió),
// y deja ver tanto las entradas como la plata de cada día. Los QR emitidos siguen
// disponibles en el selector, porque sirven para otra pregunta: cuánta gente ya sacó su
// entrada.
function VentaPorDia({ data }) {
  const [modo, setModo] = useState('cantidad');
  const vendidas = data.timelineVendidas || [];
  const serie = modo === 'emitidos' ? (data.timeline || []) : vendidas;
  const clave = modo === 'plata' ? 'recaudacion' : 'cantidad';
  const total = serie.reduce((a, d) => a + (Number(d[clave]) || 0), 0);
  const MODOS = [['cantidad', 'Entradas'], ['plata', 'Plata'], ['emitidos', 'QR emitidos']];

  return (
    <Panel title="Venta por día">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', margin: '-4px 0 12px' }}>
        <div style={{ display: 'flex', gap: 6 }}>
          {MODOS.map(([k, t]) => (
            <button key={k} className={'lb-btn lb-btn--sm ' + (modo === k ? 'lb-btn--white' : 'lb-btn--ghost')} onClick={() => setModo(k)}>{t}</button>
          ))}
        </div>
        {/* El total de la serie, para poder cruzarlo con los números de arriba sin
            tener que sumar los puntos del gráfico a ojo. */}
        <span style={{ fontSize: 12, color: INK }}>
          Total del período: <b style={{ color: '#fff' }}>{modo === 'plata' ? money(total) : total}</b>
          {modo === 'emitidos' ? ' QR emitidos' : modo === 'cantidad' ? ' vendidas' : ''}
        </span>
      </div>
      <ResponsiveContainer width="100%" height={240}>
        <BarChart data={serie}>
          <CartesianGrid stroke={GRID} vertical={false} />
          <XAxis dataKey="fecha" {...axis} />
          <YAxis {...axis} allowDecimals={false} tickFormatter={v => (modo === 'plata' ? money(v) : v)} width={modo === 'plata' ? 78 : 40} />
          <Tooltip {...tooltipStyle} cursor={{ fill: 'rgba(255,255,255,0.04)' }}
            formatter={(v, n, p) => (modo === 'plata'
              ? [money(v), 'Vendido']
              : modo === 'emitidos'
                ? [v, 'QR emitidos']
                : [`${v} · ${money(p.payload.recaudacion || 0)}`, 'Vendidas'])} />
          <Bar dataKey={clave} fill={RED} radius={[3, 3, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
      {serie.length === 0 && <div style={{ padding: '10px 0', fontSize: 13, color: INK }}>Todavía no hay ventas para este evento.</div>}
    </Panel>
  );
}

function Panel({ title, children, right }) {
  return (
    <div className="lb-card" style={{ padding: '20px 22px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div className="lb-eyebrow">{title}</div>{right}
      </div>
      {children}
    </div>
  );
}

const axis = { stroke: INK, fontSize: 11 };
const tooltipStyle = { contentStyle: { background: '#0E0E0E', border: '1px solid #2A2A2A', borderRadius: 4, color: '#fff', fontSize: 12 } };

/* ---------------- POR EVENTO ---------------- */
function PorEvento({ api, events }) {
  const [eventId, setEventId] = useState('');
  const [data, setData] = useState(null);

  useEffect(() => { if (events.length && !eventId) setEventId(events[0].id); }, [events]); // eslint-disable-line
  useEffect(() => { if (eventId) api.get(`/api/analytics/event/${eventId}`).then(setData).catch(() => setData(null)); }, [eventId]); // eslint-disable-line

  function exportCSV() {
    if (!data) return;
    const rows = data.rendimientoRrpp.map(r => ({ rrpp: r.rrpp, vendidas: r.vendidas, qr_generados: r.generadas, ingresaron: r.ingresaron, monto: r.monto, generaron_pct: r.conversion }));
    download(`${data.event.name}-rrpp.csv`, toCSV(rows.length ? rows : [{ info: 'sin datos' }]));
  }

  return (
    <div id="analytics-print">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <select className="lb-select" style={{ maxWidth: 320 }} value={eventId} onChange={e => setEventId(e.target.value)}>
          {events.map(ev => <option key={ev.id} value={ev.id}>{ev.name}</option>)}
        </select>
        <div className="no-print" style={{ display: 'flex', gap: 8 }}>
          <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={() => window.print()}>Exportar PDF</button>
          <button className="lb-btn lb-btn--white lb-btn--sm" onClick={exportCSV}>Exportar CSV</button>
        </div>
      </div>

      {!data ? <div style={{ color: INK, fontSize: 13 }}>Seleccioná un evento.</div> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div className="tk-cards" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14 }}>
            <Card label="Total vendido" value={data.resumen.vendidas} sub={(data.resumen.generadas ?? 0) + " QR generados"} />
            <Card label="Total ingresado" value={data.resumen.ingresaron} />
            <Card label="Tasa de ingreso" value={data.resumen.tasaIngreso + '%'} accent />
            <Card label="Recaudación" value={money(data.resumen.recaudacion)} accent />
          </div>

          <div className="tk-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            <Panel title="Compradores por edad">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={data.edades}>
                  <CartesianGrid stroke={GRID} vertical={false} />
                  <XAxis dataKey="rango" {...axis} /><YAxis {...axis} allowDecimals={false} />
                  <Tooltip {...tooltipStyle} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
                  <Bar dataKey="cantidad" fill={RED} radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Panel>

            <Panel title="Top localidades">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={data.localidades} layout="vertical" margin={{ left: 20 }}>
                  <XAxis type="number" {...axis} allowDecimals={false} />
                  <YAxis type="category" dataKey="ciudad" {...axis} width={90} />
                  <Tooltip {...tooltipStyle} cursor={{ fill: 'rgba(255,255,255,0.04)' }} formatter={(v, n, p) => [`${v} (${p.payload.pct}%)`, 'Compradores']} />
                  <Bar dataKey="cantidad" fill={RED2} radius={[0, 3, 3, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Panel>

            <Panel title="Venta por lote">
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={data.porLote} dataKey="cantidad" nameKey="lote" cx="50%" cy="50%" outerRadius={80} label={p => `${p.lote} ${p.pct}%`} labelLine={false} fontSize={11}>
                    {data.porLote.map((e, i) => <Cell key={i} fill={PIE[i % PIE.length]} />)}
                  </Pie>
                  <Tooltip {...tooltipStyle} formatter={(v, n, p) => [`${v} · ${money(p.payload.recaudacion)}`, p.payload.lote]} />
                </PieChart>
              </ResponsiveContainer>
            </Panel>

            <Panel title="Online vs RRPP">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={data.canal}>
                  <CartesianGrid stroke={GRID} vertical={false} />
                  <XAxis dataKey="canal" {...axis} /><YAxis {...axis} />
                  <Tooltip {...tooltipStyle} cursor={{ fill: 'rgba(255,255,255,0.04)' }} formatter={(v, n, p) => [`${money(v)} (${p.payload.pct}%)`, 'Monto']} />
                  <Bar dataKey="monto" fill={RED} radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Panel>
          </div>

          <VentaPorDia data={data} />

          <Panel title="Rendimiento de RRPP">
            <div className="lb-card tk-scroll-x" style={{ padding: '6px 0', border: 0 }}>
              <div className="lb-row lb-th" style={{ gridTemplateColumns: '1.6fr 1fr 1fr 1fr 1fr' }}>
                <span>Vendedor</span><span>Vendidas</span><span>QR generados</span><span>Monto</span><span>Generaron</span>
              </div>
              {data.rendimientoRrpp.map((r, i) => (
                <div key={i} className="lb-row" style={{ gridTemplateColumns: '1.6fr 1fr 1fr 1fr 1fr' }}>
                  <span style={{ color: '#fff', fontWeight: 600 }}>{r.rrpp}</span>
                  <span style={{ color: '#fff', fontWeight: 600 }}>{r.vendidas}</span>
                  <span style={{ color: INK }}>{r.generadas}</span>
                  <span style={{ color: '#fff' }}>{money(r.monto)}</span>
                  <span style={{ color: RED }}>{r.conversion}%</span>
                </div>
              ))}
              {data.rendimientoRrpp.length === 0 && <div style={{ padding: '16px 0', color: INK, fontSize: 13 }}>Sin ventas de RRPP.</div>}
            </div>
          </Panel>
        </div>
      )}
    </div>
  );
}

/* ---------------- ANUAL ---------------- */
function Anual({ api }) {
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(thisYear);
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/api/analytics/annual?year=${year}`).then(setData).catch(() => setData(null)); }, [year]); // eslint-disable-line

  const years = [];
  for (let y = thisYear + 1; y >= thisYear - 5; y--) years.push(y);

  function exportCSV() {
    if (!data) return;
    download(`temporada-${year}.csv`, toCSV(data.perEvent.map(e => ({
      evento: e.nombre, fecha: e.fecha, vendidas: e.vendidas, asistentes: e.asistentes,
      recaudacion: e.recaudacion, ganancia: e.ganancia, precio_promedio: e.precioPromedio,
    }))));
  }

  const MESES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

  return (
    <div id="analytics-print">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <select className="lb-select" style={{ width: 120 }} value={year} onChange={e => setYear(+e.target.value)}>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <div className="lb-display" style={{ fontSize: 22 }}>Temporada {year}</div>
        </div>
        <div className="no-print" style={{ display: 'flex', gap: 8 }}>
          <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={() => window.print()}>Exportar PDF</button>
          <button className="lb-btn lb-btn--white lb-btn--sm" onClick={exportCSV}>Exportar CSV</button>
        </div>
      </div>

      {!data ? <div style={{ color: INK, fontSize: 13 }}>Cargando…</div> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div className="tk-cards" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14 }}>
            <Card label="Eventos realizados" value={data.resumen.eventosRealizados} />
            <Card label="Entradas vendidas" value={data.resumen.entradasVendidas} />
            <Card label="Recaudación del año" value={money(data.resumen.recaudacionAnual)} accent />
            <Card label="Ganancia neta" value={money(data.resumen.gananciaNeta)} accent />
          </div>

          {data.masExitoso && (
            <div className="lb-card" style={{ padding: '22px 24px', borderColor: RED2, background: 'rgba(139,15,26,0.08)' }}>
              <div className="lb-eyebrow">Evento más exitoso del año</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: 8, flexWrap: 'wrap', gap: 8 }}>
                <div className="lb-display" style={{ fontSize: 22 }}>{data.masExitoso.nombre}</div>
                <div style={{ color: '#fff', fontFamily: 'var(--font-display)', fontWeight: 700 }}>{data.masExitoso.asistentes} asistentes</div>
              </div>
            </div>
          )}

          <div className="tk-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            <Panel title="Curva de asistencia">
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={data.perEvent}>
                  <CartesianGrid stroke={GRID} vertical={false} />
                  <XAxis dataKey="nombre" {...axis} hide /><YAxis {...axis} allowDecimals={false} />
                  <Tooltip {...tooltipStyle} labelFormatter={(l, p) => p?.[0]?.payload?.nombre || ''} />
                  <Line type="monotone" dataKey="vendidas" stroke={RED} strokeWidth={2} dot={{ r: 3, fill: RED }} />
                </LineChart>
              </ResponsiveContainer>
            </Panel>

            <Panel title="Curva de ganancia">
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={data.perEvent}>
                  <CartesianGrid stroke={GRID} vertical={false} />
                  <XAxis dataKey="nombre" {...axis} hide /><YAxis {...axis} />
                  <Tooltip {...tooltipStyle} formatter={v => money(v)} labelFormatter={(l, p) => p?.[0]?.payload?.nombre || ''} />
                  <ReferenceLine y={0} stroke={INK} strokeDasharray="4 4" />
                  <Line type="monotone" dataKey="ganancia" stroke={RED} strokeWidth={2}
                    dot={(props) => { const { cx, cy, payload } = props; return <circle cx={cx} cy={cy} r={4} fill={payload.ganancia >= 0 ? '#2ecc71' : RED} />; }} />
                </LineChart>
              </ResponsiveContainer>
            </Panel>

            <Panel title="Recaudación mensual">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={data.meses.map(m => ({ mes: MESES[m.mes - 1], recaudacion: m.recaudacion }))}>
                  <CartesianGrid stroke={GRID} vertical={false} />
                  <XAxis dataKey="mes" {...axis} /><YAxis {...axis} />
                  <Tooltip {...tooltipStyle} cursor={{ fill: 'rgba(255,255,255,0.04)' }} formatter={v => money(v)} />
                  <Bar dataKey="recaudacion" fill={RED} radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Panel>

            <Panel title="Top localidades del año">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={data.localidades} layout="vertical" margin={{ left: 20 }}>
                  <XAxis type="number" {...axis} allowDecimals={false} />
                  <YAxis type="category" dataKey="ciudad" {...axis} width={90} />
                  <Tooltip {...tooltipStyle} cursor={{ fill: 'rgba(255,255,255,0.04)' }} formatter={(v, n, p) => [`${v} (${p.payload.pct}%)`, 'Compradores']} />
                  <Bar dataKey="cantidad" fill={RED2} radius={[0, 3, 3, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Panel>
          </div>

          <Panel title="Precio promedio por entrada">
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={data.perEvent}>
                <CartesianGrid stroke={GRID} vertical={false} />
                <XAxis dataKey="nombre" {...axis} hide /><YAxis {...axis} />
                <Tooltip {...tooltipStyle} formatter={v => money(v)} labelFormatter={(l, p) => p?.[0]?.payload?.nombre || ''} />
                <Line type="monotone" dataKey="precioPromedio" stroke="#E63946" strokeWidth={2} dot={{ r: 3, fill: '#E63946' }} />
              </LineChart>
            </ResponsiveContainer>
          </Panel>
        </div>
      )}
    </div>
  );
}

/* ---------------- API REST ---------------- */
function ApiSection({ api }) {
  const [org, setOrg] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => { api.get('/api/org').then(setOrg).catch(() => {}); }, []); // eslint-disable-line

  async function generate() {
    if (org?.api_key && !window.confirm('Ya hay una API key. ¿Regenerarla? La anterior dejará de funcionar.')) return;
    setGenerating(true);
    try { const r = await api.post('/api/org/api-key'); setOrg(o => ({ ...o, api_key: r.api_key })); }
    catch (e) { alert(e.message); } finally { setGenerating(false); }
  }

  const ENDPOINTS = [
    'GET /api/v1/events',
    'GET /api/v1/events/:id/sales',
    'GET /api/v1/events/:id/buyers',
    'GET /api/v1/events/:id/attendance',
    'GET /api/v1/events/:id/rrpp',
    'GET /api/v1/analytics/annual?year=XXXX',
  ];

  return (
    <div style={{ maxWidth: 720 }}>
      <div className="lb-display" style={{ fontSize: 22, marginBottom: 6 }}>API REST</div>
      <p style={{ color: INK, fontSize: 14, marginTop: 0 }}>Consultá tus datos de analítica desde sistemas externos. Autenticá con el header <code style={{ color: '#fff' }}>X-API-Key</code>.</p>

      <div className="lb-card" style={{ padding: '20px 22px', marginTop: 16 }}>
        <div className="lb-eyebrow" style={{ marginBottom: 10 }}>Tu API key</div>
        {org?.api_key ? (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <code style={{ flex: 1, minWidth: 240, background: 'var(--ink-050)', border: '1px solid var(--border)', borderRadius: 4, padding: '10px 12px', color: '#fff', fontSize: 13, wordBreak: 'break-all' }}>{org.api_key}</code>
            <button className="lb-btn lb-btn--white lb-btn--sm" onClick={() => { navigator.clipboard?.writeText(org.api_key); setCopied(true); setTimeout(() => setCopied(false), 1500); }}>{copied ? 'Copiado ✓' : 'Copiar'}</button>
            <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={generate} disabled={generating}>Regenerar</button>
          </div>
        ) : (
          <button className="lb-btn lb-btn--primary lb-btn--sm" onClick={generate} disabled={generating}>{generating ? 'Generando…' : 'Generar API key'}</button>
        )}
      </div>

      <div className="lb-card" style={{ padding: '20px 22px', marginTop: 16 }}>
        <div className="lb-eyebrow" style={{ marginBottom: 12 }}>Endpoints</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontFamily: 'monospace', fontSize: 13 }}>
          {ENDPOINTS.map(e => <div key={e} style={{ color: 'var(--ink-800)' }}><span style={{ color: RED }}>GET</span> {e.replace('GET ', '')}</div>)}
        </div>
        <div style={{ marginTop: 14, fontSize: 12, color: INK }}>
          Ejemplo: <code style={{ color: '#fff' }}>curl -H "X-API-Key: TU_KEY" {typeof window !== 'undefined' ? window.location.origin : ''}/api/v1/events</code>
        </div>
      </div>
    </div>
  );
}
